import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import LoginView from '../views/LoginView.vue'
import { useAuthStore } from '../stores/authStore'
import { logger } from '../utils/logger'
import ErrorView from '../views/ErrorView.vue'

// Async component imports with error handling
const AnalyzeView = () => import('../views/AnalyzeView.vue')
const MyPlantsView = () => import('../views/MyPlantsView.vue')
const PlantDetailsView = () => import('../views/PlantDetailsView.vue')
const PlantChatView = () => import('../views/PlantChatView.vue')
const AboutView = () => import('../views/AboutView.vue')
const ContactView = () => import('../views/ContactView.vue')
const RegisterView = () => import('../views/RegisterView.vue')
// Remove the separate const definition for ForgotPasswordView
const PrivacyPolicyView = () => import('../views/PrivacyPolicyView.vue')
const TermsOfUseView = () => import('../views/TermsOfUseView.vue')
const ProfileView = () => import('../views/ProfileView.vue')

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView
    },
    {
      path: '/analyze',
      name: 'analyze',
      component: AnalyzeView,
      beforeEnter: (to, from, next) => {
        // Store the attempted route for retry functionality
        sessionStorage.setItem('lastAttemptedRoute', to.fullPath)
        next()
      }
    },
    {
      path: '/my-plants',
      name: 'my-plants',
      component: MyPlantsView,
      meta: { requiresAuth: true }
    },
    {
      path: '/plants/:id',
      name: 'plant-details',
      component: PlantDetailsView,
      meta: { requiresAuth: true }
    },
    {
      path: '/plants/:id/chat',
      name: 'plant-chat',
      component: PlantChatView,
      meta: { requiresAuth: true }
    },
    {
      path: '/about',
      name: 'about',
      component: AboutView
    },
    {
      path: '/contact',
      name: 'contact',
      component: ContactView
    },
    {
      path: '/login',
      name: 'login',
      component: LoginView,
      meta: { hideForAuth: true }
    },
    {
      path: '/register',
      name: 'register',
      component: RegisterView,
      meta: { hideForAuth: true }
    },
    { // Add route for Forgot Password using inline dynamic import
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('../views/ForgotPasswordView.vue'),
      meta: { hideForAuth: true } // Hide if user is already logged in
    },
    { // Add route for Reset Password
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('../views/ResetPasswordView.vue'),
      meta: { hideForAuth: true } // Hide if user is already logged in
    },
    {
      path: '/privacy-policy',
      name: 'privacy-policy',
      component: PrivacyPolicyView
    },
    {
      path: '/terms-of-use',
      name: 'terms-of-use',
      component: TermsOfUseView
    },
    {
      path: '/profile',
      name: 'profile',
      component: ProfileView,
      meta: { requiresAuth: true }
    },
    {
      path: '/error',
      name: 'error',
      component: ErrorView
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('../views/SettingsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/error'
    }
  ]
})

// Global error handler for component loading failures
router.onError((error) => {
  logger.error('Router error:', error)
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    router.push('/error')
  }
})

// Navigation guard for protected routes
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Initialize auth if not already done
  if (authStore.token && !authStore.user) {
    logger.debug('Router guard: Token exists but no user, initializing auth')
    try {
      const success = await authStore.initAuth()
      logger.debug(`Auth initialization ${success ? 'successful' : 'failed'}`)
      
      // If auth initialization failed but we're going to a protected route,
      // redirect to login
      if (!success && to.meta.requiresAuth) {
        logger.warn('Auth initialization failed for protected route, redirecting to login')
        return next({ name: 'login', query: { redirect: to.fullPath } })
      }
    } catch (error) {
      logger.error('Error in router guard during auth initialization:', error)
      // Clear auth on error and redirect if needed
      authStore.clearAuth()
      if (to.meta.requiresAuth) {
        return next({ name: 'login', query: { redirect: to.fullPath } })
      }
    }
  }
  
  const isAuthenticated = authStore.isAuthenticated && authStore.user
  
  // Handle login page access
  if (to.name === 'login') {
    if (isAuthenticated) {
      logger.debug('User is authenticated, redirecting to home')
      return next({ name: 'home' })
    }
    return next()
  }
  
  // Handle protected routes
  if (to.meta.requiresAuth && !isAuthenticated) {
    logger.debug(`Redirecting to login: Protected route ${to.path} requires authentication`)
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }
  
  // Handle auth-only pages
  if (to.meta.hideForAuth && isAuthenticated) {
    logger.debug(`Redirecting to home: Route ${to.path} should be hidden for authenticated users`)
    return next({ name: 'home' })
  }
  
  // Handle 404 page
  if (to.name === 'not-found') {
    logger.debug('Accessing 404 page')
    return next()
  }
  
  next()
})

export default router
